/**
 * useSectionLibrary Hook
 *
 * Manages fetching and searching the sections library.
 * Provides sections grouped by scope with search functionality.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import {
  useGetSectionLibraryQuery,
  useSearchSectionsMutation,
} from '../store/sectionsApi';
import type { SectionLibraryWithMeta } from '../types';
import { toSectionLibraryWithMeta } from '../types';

interface UseSectionLibraryReturn {
  /** All accessible sections with metadata */
  sections: SectionLibraryWithMeta[];
  /** Whether sections are loading */
  isLoading: boolean;
  /** Search term */
  searchTerm: string;
  /** Set search term */
  setSearchTerm: (term: string) => void;
  /** Search results (null if not searching) */
  searchResults: SectionLibraryWithMeta[] | null;
  /** Whether search is in progress */
  isSearching: boolean;
  /** Selected section for preview */
  selectedSection: SectionLibraryWithMeta | null;
  /** Select a section */
  selectSection: (section: SectionLibraryWithMeta) => void;
  /** Clear selection */
  clearSelection: () => void;
  /** Refetch sections */
  refetch: () => void;
}

export const useSectionLibrary = (): UseSectionLibraryReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState<SectionLibraryWithMeta | null>(null);

  // Fetch all accessible sections
  const {
    data: sectionsData,
    isLoading,
    refetch: refetchSections,
  } = useGetSectionLibraryQuery();

  // Search mutation
  const [searchSections, { isLoading: isSearching }] = useSearchSectionsMutation();

  // Transform sections to include metadata
  const sections = useMemo((): SectionLibraryWithMeta[] => {
    if (!sectionsData?.sections) return [];
    return sectionsData.sections.map(s => toSectionLibraryWithMeta(s));
  }, [sectionsData]);

  // Search results
  const [searchResults, setSearchResults] = useState<SectionLibraryWithMeta[] | null>(null);

  // Perform search when search term changes
  const handleSearch = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setSearchResults(null);
        return;
      }

      try {
        const result = await searchSections({
          searchSectionsRequest: {
            query: term.trim(),
            categories: null,
          },
        }).unwrap();

        const transformed = result.sections.map(s => toSectionLibraryWithMeta(s));
        setSearchResults(transformed);
      } catch (error) {
        console.error('Failed to search sections:', error);
        enqueueSnackbar('Failed to search sections', { variant: 'error' });
        setSearchResults([]);
      }
    },
    [searchSections, enqueueSnackbar],
  );

  // Update search term and trigger search
  const handleSetSearchTerm = useCallback(
    (term: string) => {
      setSearchTerm(term);
      void handleSearch(term);
    },
    [handleSearch],
  );

  // Select section
  const selectSection = useCallback((section: SectionLibraryWithMeta) => {
    setSelectedSection(section);
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedSection(null);
  }, []);

  // Refetch
  const refetch = useCallback(() => {
    void refetchSections();
  }, [refetchSections]);

  return {
    sections,
    isLoading,
    searchTerm,
    setSearchTerm: handleSetSearchTerm,
    searchResults,
    isSearching,
    selectedSection,
    selectSection,
    clearSelection,
    refetch,
  };
};

